// Builds per-page <head> metadata: title/description, Open Graph tags, the
// canonical URL, and hreflang alternates for every localized variant of the
// page. Every route's head() funnels through pageHead() so SEO tags stay
// consistent across all 81 localized routes.

import { locales, ogLocale, type Locale } from "./config";
import { hasLocalizedPath, slugMap, pathFor, type PageId } from "./slugMap";
import { BASE_URL } from "./site";
import { breadcrumbList, ldScript } from "@/lib/schema";

export { BASE_URL };

export type PageMeta = {
  title: string;
  description: string;
  ogImage?: string;
};

/** Strip the " | MI Support" / " - MI Support" suffix for breadcrumb names. */
function cleanName(title: string): string {
  return title.replace(/\s*[|–-]\s*MI Support.*$/i, "").trim() || title;
}

export function pageHead(
  id: PageId,
  locale: Locale,
  meta: PageMeta,
  opts?: { breadcrumbName?: string; schema?: unknown[] },
) {
  const abs = (path: string) => BASE_URL + path;
  const canonical = abs(slugMap[id][locale] ?? slugMap[id].da);

  // hreflang alternates: only for locales that actually have a localized route
  // (during phased rollout en/de may not exist yet). x-default points at Danish.
  const alternates = locales
    .filter((l) => hasLocalizedPath(id, l))
    .map((l) => ({ rel: "alternate", hreflang: l, href: abs(slugMap[id][l]!) }));

  const links = [
    { rel: "canonical", href: canonical },
    ...alternates,
    { rel: "alternate", hreflang: "x-default", href: abs(slugMap[id].da) },
  ];

  const meta_tags = [
    { title: meta.title },
    { name: "description", content: meta.description },
    { property: "og:title", content: meta.title },
    { property: "og:description", content: meta.description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonical },
    { property: "og:locale", content: ogLocale[locale] },
    ...locales
      .filter((l) => l !== locale)
      .map((l) => ({ property: "og:locale:alternate", content: ogLocale[l] })),
    ...(meta.ogImage ? [{ property: "og:image", content: meta.ogImage }] : []),
  ];

  // BreadcrumbList for every page except the home page.
  const scripts: { type: string; children: string }[] = [];
  if (id !== "home") {
    scripts.push(
      ldScript(
        breadcrumbList([
          { name: "MI Support", url: abs(pathFor("home", locale)) },
          { name: opts?.breadcrumbName ?? cleanName(meta.title), url: canonical },
        ]),
      ),
    );
  }
  for (const s of opts?.schema ?? []) scripts.push(ldScript(s));

  return { meta: meta_tags, links, scripts };
}
